import { useEffect, useState, type FormEvent } from "react";
import * as turf from "@turf/turf";
import {
  AlertTriangle,
  CircleHelp,
  Crosshair,
  Database,
  History,
  Layers3,
  MapPinPlus,
  Play,
  Ruler,
  Settings2,
  Square,
  Trash2,
  Users,
  WifiOff,
  X,
} from "lucide-react";
import {
  deriveTimer,
  formatDuration,
  type DatasetCategory,
  type GameState,
  type MapFeature,
  type MapFeatureCollection,
  type PublicConfig,
  type QuestionInstance,
  type SeekerMarker,
} from "@hideseek/shared";
import { api, patch, post, remove } from "../api";
import { GameMap, type MapLayers } from "./GameMap";
import { MapLegendModal } from "./MapLegendModal";
import { QuestionActivitySidebar, QuestionComposer, QuestionHistory } from "./QuestionPanel";

interface Props {
  state: GameState;
  config: PublicConfig;
  token: string;
  connected: boolean;
  refresh: () => Promise<void>;
  onIdentityCleared: () => void;
  onError: (message: string | null) => void;
}

type Panel = "history" | "layers" | "data" | "game" | "legend" | null;
type Interaction = "measure" | "marker" | "question-a" | "question-b" | null;

function gameTimer(state: GameState, currentTime: Date): { label: string; value: string } {
  const game = state.game;
  if (game.phase === "ENDED") {
    const seconds =
      game.phaseStartedAt && game.endedAt
        ? Math.max(
            0,
            Math.floor(
              (new Date(game.endedAt).getTime() - new Date(game.phaseStartedAt).getTime()) / 1000,
            ),
          )
        : 0;
    return { label: "Final seeking time", value: formatDuration(seconds) };
  }
  const timer = deriveTimer(
    game.phase,
    game.hidingDurationSeconds,
    game.phaseStartedAt,
    currentTime,
  );
  return timer.phase === "HIDING" || timer.phase === "LOBBY"
    ? {
        label: game.phase === "LOBBY" ? "Hiding time" : "Hiding",
        value: formatDuration(timer.remainingSeconds),
      }
    : { label: "Seeking", value: formatDuration(timer.elapsedSeekingSeconds) };
}

export function GameScreen({
  state,
  config,
  token,
  connected,
  refresh,
  onIdentityCleared,
  onError,
}: Props) {
  const [panel, setPanel] = useState<Panel>(null);
  const [composer, setComposer] = useState<{ open: boolean; question: QuestionInstance | null }>({
    open: false,
    question: null,
  });
  const [layers, setLayers] = useState<MapLayers>({
    possibleArea: true,
    questionGeometry: true,
    administrative: false,
    transitLines: true,
    transitStations: true,
    datasets: false,
    markers: true,
  });
  const [localPosition, setLocalPosition] = useState<[number, number] | null>(null);
  const [locationStatus, setLocationStatus] = useState<
    "idle" | "requesting" | "available" | "blocked"
  >("idle");
  const [locationMessage, setLocationMessage] = useState<string | null>(null);
  const [measurement, setMeasurement] = useState<[number, number][]>([]);
  const [interaction, setInteraction] = useState<Interaction>(null);
  const [pickedQuestionPoint, setPickedQuestionPoint] = useState<{
    target: "A" | "B";
    point: [number, number];
    nonce: number;
  } | null>(null);
  const [draftQuestionPoints, setDraftQuestionPoints] = useState<
    Partial<Record<"A" | "B", [number, number]>>
  >({});
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [historyInspectedId, setHistoryInspectedId] = useState<string | null>(null);
  const [draftQuestionGeometry, setDraftQuestionGeometry] = useState<
    MapFeatureCollection | MapFeature | null
  >(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [gameActionBusy, setGameActionBusy] = useState(false);
  const [markerDraft, setMarkerDraft] = useState<{
    point: [number, number];
    title: string;
    note: string;
    id?: string;
  } | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!("permissions" in navigator)) return;
    void navigator.permissions
      .query({ name: "geolocation" })
      .then((permission) => {
        if (permission.state === "denied") {
          setLocationStatus("blocked");
          setLocationMessage(
            "Location is blocked for this site. Allow it in the browser's site settings, then try again.",
          );
        }
      })
      .catch(() => undefined);
  }, []);

  const timer = gameTimer(state, currentTime);
  const distance =
    measurement.length === 2
      ? turf.distance(measurement[0]!, measurement[1]!, { units: "kilometers" })
      : null;
  const activeQuestions = state.questions.filter((question) => question.status !== "APPLIED");

  useEffect(() => {
    if (activeQuestions.length > 0) {
      const isSelectedActive = activeQuestions.some((q) => q.id === selectedQuestionId);
      if (!isSelectedActive) {
        setSelectedQuestionId(activeQuestions[0]!.id);
      }
    } else if (selectedQuestionId && selectedQuestionId !== historyInspectedId) {
      setSelectedQuestionId(null);
    }
  }, [activeQuestions, selectedQuestionId, historyInspectedId]);

  function requestGps(questionTarget?: "A" | "B") {
    if (!window.isSecureContext) {
      const message =
        "Location requires a secure HTTPS address on iPad and other mobile browsers. Open this app through its HTTPS URL.";
      setLocationStatus("blocked");
      setLocationMessage(message);
      onError(message);
      return;
    }
    if (!navigator.geolocation) {
      const message = "Geolocation is not supported by this browser.";
      setLocationStatus("blocked");
      setLocationMessage(message);
      onError(message);
      return;
    }
    setLocationStatus("requesting");
    setLocationMessage(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Architectural privacy boundary: this coordinate remains React-local and is only passed to GameMap.
        const point: [number, number] = [position.coords.longitude, position.coords.latitude];
        setLocalPosition(point);
        setLocationStatus("available");
        setLocationMessage(null);
        if (questionTarget) {
          setPickedQuestionPoint({ target: questionTarget, point, nonce: Date.now() });
          setDraftQuestionPoints((current) => ({ ...current, [questionTarget]: point }));
        }
        onError(null);
      },
      (error) => {
        const embedded = window.top !== window.self;
        const message =
          error.code === error.PERMISSION_DENIED
            ? embedded
              ? "Location is blocked in this embedded view. Open the app directly in its own HTTPS tab and allow location access."
              : "Location is blocked for this site. Allow location in the browser's site settings, then reload and try again."
            : `Local GPS unavailable: ${error.message}`;
        setLocationStatus("blocked");
        setLocationMessage(message);
        onError(message);
      },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 15_000 },
    );
  }

  function mapClick(point: [number, number]) {
    if (interaction === "measure") {
      setMeasurement((old) => (old.length >= 2 ? [point] : [...old, point]));
    } else if (interaction === "marker" && state.me.role === "SEEKER") {
      setMarkerDraft({ point, title: "", note: "" });
      setInteraction(null);
    } else if (interaction === "question-a" || interaction === "question-b") {
      const target = interaction === "question-a" ? "A" : "B";
      setPickedQuestionPoint({
        target,
        point,
        nonce: Date.now(),
      });
      setDraftQuestionPoints((current) => ({ ...current, [target]: point }));
      setInteraction(null);
    }
  }

  async function gameAction(
    path: string,
    method: "POST" | "DELETE" = "POST",
    body: Record<string, unknown> = {},
  ) {
    setGameActionBusy(true);
    try {
      if (method === "DELETE") await remove(path, token);
      else await post(path, body, token);
      await refresh();
      onError(null);
    } catch (cause) {
      onError((cause as Error).message);
    } finally {
      setGameActionBusy(false);
    }
  }

  async function saveMarker(event: FormEvent) {
    event.preventDefault();
    if (!markerDraft) return;
    const body = {
      position: turf.point(markerDraft.point),
      title: markerDraft.title,
      note: markerDraft.note || null,
    };
    try {
      if (markerDraft.id) await patch(`/api/markers/${markerDraft.id}`, body, token);
      else await post("/api/markers", body, token);
      setMarkerDraft(null);
      await refresh();
    } catch (cause) {
      onError((cause as Error).message);
    }
  }

  function editMarker(marker: SeekerMarker) {
    setMarkerDraft({
      point: marker.position.geometry.coordinates as [number, number],
      title: marker.title,
      note: marker.note ?? "",
      id: marker.id,
    });
  }

  return (
    <main className="game-shell">
      <header className="game-header">
        <div className="game-title">
          <p className="eyebrow">
            {state.game.seekerOnly
              ? "Seeker-only board"
              : state.me.role === "SEEKER"
                ? "Seeker board"
                : "Hider board"}
          </p>
          <h1>{state.game.name}</h1>
        </div>
        <div className="timer" aria-label={`${timer.label} ${timer.value}`}>
          <span>{timer.label}</span>
          <strong>{timer.value}</strong>
        </div>
        <button className="header-action" onClick={() => setPanel("game")}>
          <Users size={20} />
          <span>
            {state.players.filter((player) => player.connected).length}/{state.players.length}
          </span>
        </button>
        <button
          className="header-action"
          onClick={() => setPanel("legend")}
          aria-label="Map guide and legend"
          title="Map guide and legend"
        >
          <CircleHelp size={20} />
        </button>
        <button
          className="header-action"
          onClick={() => setPanel("layers")}
          aria-label="Map layers"
          title="Map layers"
        >
          <Layers3 size={20} />
        </button>
      </header>
      {!connected && (
        <div className="connection-banner">
          <WifiOff size={16} />
          Reconnecting — displayed shared state may be out of date.
        </div>
      )}
      {!state.game.possibleArea && (
        <div className="connection-banner geometry-warning" role="alert">
          <AlertTriangle size={16} />
          No Possible Area remains. Review, correct, disable, or delete an applied answer.
        </div>
      )}
      <section className="map-stage">
        <GameMap
          state={state}
          config={config}
          layers={layers}
          localPosition={localPosition}
          measurement={measurement}
          draftQuestionActive={composer.open}
          draftQuestionPoints={draftQuestionPoints}
          draftQuestionGeometry={draftQuestionGeometry}
          selectedQuestionId={selectedQuestionId}
          interactionActive={interaction !== null}
          onMapClick={mapClick}
        />
        <div className="map-tools">
          <button
            className={interaction === "measure" ? "map-tool active" : "map-tool"}
            onClick={() => {
              setInteraction(interaction === "measure" ? null : "measure");
              setMeasurement([]);
            }}
            aria-label="Measure distance"
            title="Measure distance"
          >
            <Ruler />
          </button>
          {state.me.role === "SEEKER" && (
            <button
              className={interaction === "marker" ? "map-tool active" : "map-tool"}
              onClick={() => setInteraction(interaction === "marker" ? null : "marker")}
              aria-label="Place Seeker marker"
              title="Place Seeker marker"
            >
              <MapPinPlus />
            </button>
          )}
          <button
            className="map-tool"
            onClick={() => requestGps()}
            aria-label="Show my location locally"
            title="Show my location locally"
          >
            <Crosshair />
          </button>
          <button
            className="map-tool"
            onClick={() => setPanel("legend")}
            aria-label="Map guide and legend"
            title="Map guide and legend"
          >
            <CircleHelp />
          </button>
        </div>
        {!localPosition && (
          <div
            className={`location-card ${locationStatus === "blocked" ? "blocked" : ""}`}
            aria-live="polite"
          >
            {locationMessage && <span>{locationMessage}</span>}
            <button
              className="button secondary small-button"
              disabled={locationStatus === "requesting"}
              onClick={() => requestGps()}
            >
              <Crosshair size={16} />{" "}
              {locationStatus === "requesting" ? "Requesting…" : "Enable location"}
            </button>
          </div>
        )}
        {interaction && (
          <div className="interaction-hint">
            {interaction === "measure"
              ? `Tap ${measurement.length === 0 ? "point A" : measurement.length === 1 ? "point B" : "to start again"}`
              : interaction === "marker"
                ? "Tap the map to place a Seeker marker"
                : `Tap the map to choose ${interaction === "question-a" ? "the reference point" : "the end point"}`}
            <button onClick={() => setInteraction(null)} aria-label="Cancel">
              <X size={16} />
            </button>
          </div>
        )}
        {distance !== null && (
          <div className="measurement-result">
            <Ruler size={17} />
            <strong>
              {distance < 1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(2)} km`}
            </strong>
            <button onClick={() => setMeasurement([])}>Reset</button>
          </div>
        )}
      </section>

      <div className="action-rail">
        <nav className="bottom-bar" aria-label="Game actions">
          <button onClick={() => setPanel("history")}>
            <History />
            <span>History</span>
          </button>
          {state.me.role === "SEEKER" && (
            <button
              className="ask-button"
              onClick={() => {
                setPickedQuestionPoint(null);
                setDraftQuestionPoints({});
                setInteraction(null);
                setComposer({ open: true, question: null });
              }}
            >
              <CircleHelp />
              <span>Ask question</span>
            </button>
          )}
          <button onClick={() => setPanel("data")}>
            <Database />
            <span>Data</span>
          </button>
        </nav>

        {panel === null && !composer.open && activeQuestions.length > 0 && (
          <QuestionActivitySidebar
            state={state}
            token={token}
            refresh={refresh}
            onError={onError}
            questions={activeQuestions}
            localPosition={localPosition}
            onRequestGps={() => requestGps()}
            onSelect={(id) => {
              setSelectedQuestionId(id);
              setHistoryInspectedId(null);
            }}
            onEdit={(question) => {
              setPickedQuestionPoint(null);
              setDraftQuestionPoints({});
              setInteraction(null);
              setComposer({ open: true, question });
            }}
          />
        )}
        {composer.open && (
          <QuestionComposer
            state={state}
            token={token}
            refresh={refresh}
            onError={onError}
            question={composer.question}
            localPosition={localPosition}
            pickedPoint={pickedQuestionPoint}
            pickingFromMap={interaction === "question-a" || interaction === "question-b"}
            onPickFromMap={(target) => setInteraction(target === "A" ? "question-a" : "question-b")}
            onRequestGps={requestGps}
            onPreviewChange={setDraftQuestionGeometry}
            onSelect={setSelectedQuestionId}
            onPointPicked={(target, point) =>
              setDraftQuestionPoints((curr) => ({ ...curr, [target]: point }))
            }
            onClose={() => {
              setComposer({ open: false, question: null });
              setPickedQuestionPoint(null);
              setDraftQuestionPoints({});
              setInteraction(null);
              setDraftQuestionGeometry(null);
            }}
          />
        )}
      </div>

      {panel === "history" && (
        <QuestionHistory
          state={state}
          token={token}
          refresh={refresh}
          onError={onError}
          onClose={() => setPanel(null)}
          onSelect={(id) => {
            setSelectedQuestionId(id);
            setHistoryInspectedId(id);
            setPanel(null);
          }}
          onEdit={(question) => {
            setPanel(null);
            setPickedQuestionPoint(null);
            setDraftQuestionPoints({});
            setInteraction(null);
            setComposer({ open: true, question });
          }}
        />
      )}
      {panel === "layers" && (
        <LayerPanel
          layers={layers}
          setLayers={setLayers}
          onClose={() => setPanel(null)}
          onOpenLegend={() => setPanel("legend")}
        />
      )}
      {panel === "legend" && (
        <MapLegendModal onClose={() => setPanel(null)} onOpenLayers={() => setPanel("layers")} />
      )}
      {panel === "data" && (
        <DataPanel
          state={state}
          token={token}
          config={config}
          refresh={refresh}
          onError={onError}
          onClose={() => setPanel(null)}
          onEditMarker={editMarker}
          onStartMarker={() => {
            setPanel(null);
            setInteraction("marker");
          }}
        />
      )}
      {panel === "game" && (
        <GamePanel
          state={state}
          busy={gameActionBusy}
          onClose={() => setPanel(null)}
          onStart={(loadOsmPois) => gameAction("/api/game/start", "POST", { loadOsmPois })}
          onEnd={() => {
            if (
              window.confirm(
                "End this game? The final board remains available until it is cleared.",
              )
            )
              void gameAction("/api/game/end");
          }}
          onReset={() => {
            const message =
              state.game.phase === "LOBBY"
                ? "Discard this unstarted game setup and all joined players so you can create it again?"
                : "Permanently clear the ended game and all its shared data?";
            if (window.confirm(message)) void gameAction("/api/game", "DELETE");
          }}
          onLeaveDevice={() => {
            onIdentityCleared();
          }}
        />
      )}
      {markerDraft && (
        <div className="modal-backdrop">
          <form className="sheet marker-sheet" onSubmit={saveMarker}>
            <div className="sheet-header">
              <div>
                <p className="eyebrow">Seeker team only</p>
                <h2>{markerDraft.id ? "Edit marker" : "New marker"}</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setMarkerDraft(null)}>
                <X />
              </button>
            </div>
            <label>
              Title
              <input
                value={markerDraft.title}
                onChange={(event) => setMarkerDraft({ ...markerDraft, title: event.target.value })}
                maxLength={80}
                autoFocus
                required
              />
            </label>
            <label>
              Note
              <textarea
                value={markerDraft.note}
                onChange={(event) => setMarkerDraft({ ...markerDraft, note: event.target.value })}
                maxLength={500}
              />
            </label>
            <p className="field-help">
              {markerDraft.point[1].toFixed(5)}, {markerDraft.point[0].toFixed(5)}
            </p>
            <button className="button primary">Save marker</button>
          </form>
        </div>
      )}
    </main>
  );
}

function LayerPanel({
  layers,
  setLayers,
  onClose,
  onOpenLegend,
}: {
  layers: MapLayers;
  setLayers: (layers: MapLayers) => void;
  onClose: () => void;
  onOpenLegend: () => void;
}) {
  const labels: Record<keyof MapLayers, string> = {
    possibleArea: "Possible Area",
    questionGeometry: "Question Geometry",
    administrative: "Administrative Boundaries",
    transitLines: "Transit Lines",
    transitStations: "Transit Stations",
    datasets: "Imported Datasets",
    markers: "Personal / Seeker Markers",
  };
  return (
    <div className="modal-backdrop align-end">
      <section className="sheet compact-sheet">
        <div className="sheet-header">
          <h2>Map layers</h2>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </div>
        {(Object.keys(layers) as Array<keyof MapLayers>).map((key) => (
          <label className="switch-row" key={key}>
            <input
              type="checkbox"
              checked={layers[key]}
              onChange={(event) => setLayers({ ...layers, [key]: event.target.checked })}
            />
            <span>
              <strong>{labels[key]}</strong>
            </span>
          </label>
        ))}
        <div className="layer-panel-footer">
          <button type="button" className="button secondary wide" onClick={onOpenLegend}>
            <CircleHelp size={16} />
            Map guide & legend
          </button>
        </div>
      </section>
    </div>
  );
}

interface DataPanelProps {
  state: GameState;
  token: string;
  config: PublicConfig;
  refresh: () => Promise<void>;
  onError: (message: string | null) => void;
  onClose: () => void;
  onEditMarker: (marker: SeekerMarker) => void;
  onStartMarker: () => void;
}

function DataPanel({
  state,
  token,
  config,
  refresh,
  onError,
  onClose,
  onEditMarker,
  onStartMarker,
}: DataPanelProps) {
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState<DatasetCategory>("OTHER");
  const [file, setFile] = useState<File | null>(null);
  const [replacementId, setReplacementId] = useState<string | null>(null);

  async function load(path: string) {
    setBusy(true);
    try {
      const body = path.endsWith("subdivisions")
        ? { adminLevel: state.game.firstDivisionAdminLevel }
        : {};
      if (path.endsWith("subdivisions") && !state.game.firstDivisionAdminLevel)
        throw new Error("Choose the First Division areas first.");
      await post(path, body, token);
      await refresh();
    } catch (cause) {
      onError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function upload(event: FormEvent) {
    event.preventDefault();
    if (!file) return;
    const form = new FormData();
    form.append("name", name);
    form.append("category", category);
    form.append("file", file);
    setBusy(true);
    try {
      await api("/api/datasets", { method: "POST", body: form }, token);
      setName("");
      setFile(null);
      await refresh();
    } catch (cause) {
      onError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteDataset(id: string, datasetName: string) {
    if (!window.confirm(`Delete dataset “${datasetName}”?`)) return;
    try {
      await remove(`/api/datasets/${id}`, token);
      await refresh();
    } catch (cause) {
      onError((cause as Error).message);
    }
  }

  async function replaceDataset(id: string, replacement: File | null) {
    if (!replacement) return;
    const form = new FormData();
    form.append("file", replacement);
    setReplacementId(id);
    try {
      await api(`/api/datasets/${id}/content`, { method: "PUT", body: form }, token);
      await refresh();
    } catch (cause) {
      onError((cause as Error).message);
    } finally {
      setReplacementId(null);
    }
  }

  async function changeDatasetCategory(id: string, nextCategory: DatasetCategory) {
    try {
      await patch(`/api/datasets/${id}`, { category: nextCategory }, token);
      await refresh();
    } catch (cause) {
      onError((cause as Error).message);
    }
  }

  async function deleteMarker(id: string) {
    if (!window.confirm("Delete this Seeker marker?")) return;
    try {
      await remove(`/api/markers/${id}`, token);
      await refresh();
    } catch (cause) {
      onError((cause as Error).message);
    }
  }

  return (
    <div className="modal-backdrop align-end">
      <section className="sheet data-sheet">
        <div className="sheet-header">
          <div>
            <p className="eyebrow">Cached with this game</p>
            <h2>Map data & notes</h2>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </div>
        <div className="data-section">
          <h3>OpenStreetMap layers</h3>
          <p>Already cached data stays usable if Overpass is unavailable.</p>
          {state.me.role === "SEEKER" && (
            <div className="button-row">
              <button
                className="button secondary"
                disabled={busy}
                onClick={() => void load("/api/game/subdivisions")}
              >
                {state.game.subdivisions ? "Reload First Division" : "Load First Division"}
              </button>
              <button
                className="button secondary"
                disabled={busy}
                onClick={() => void load("/api/game/transit")}
              >
                {state.game.transitLines ? "Reload transit" : "Load transit"}
              </button>
            </div>
          )}
          <small>
            {state.game.subdivisions?.features.length ?? 0} divisions ·{" "}
            {state.game.transitLines?.features.length ?? 0} lines ·{" "}
            {state.game.transitStations?.features.length ?? 0} stations
          </small>
        </div>
        <div className="data-section">
          <h3>Place datasets</h3>
          {state.datasets.map((dataset) => (
            <div className="data-row" key={dataset.id}>
              <div>
                <strong>{dataset.name}</strong>
                {dataset.temporary && (
                  <small>OpenStreetMap fallback · removed when the game ends</small>
                )}
                {state.me.role === "SEEKER" ? (
                  <select
                    className="inline-select"
                    value={dataset.category}
                    aria-label={`Category for ${dataset.name}`}
                    onChange={(event) =>
                      void changeDatasetCategory(dataset.id, event.target.value as DatasetCategory)
                    }
                  >
                    {["TENTACLES", "MATCHING", "MEASURING", "TRANSIT", "OTHER"].map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                ) : (
                  <small>{dataset.category}</small>
                )}
                <small>{dataset.featureCount} features</small>
              </div>
              {state.me.role === "SEEKER" && (
                <div className="row-actions">
                  {!dataset.temporary && (
                    <label className="text-button file-button">
                      {replacementId === dataset.id ? "Replacing…" : "Replace"}
                      <input
                        type="file"
                        accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                        disabled={replacementId !== null}
                        onChange={(event) =>
                          void replaceDataset(dataset.id, event.target.files?.[0] ?? null)
                        }
                      />
                    </label>
                  )}
                  <button
                    className="icon-button danger small"
                    onClick={() => void deleteDataset(dataset.id, dataset.name)}
                  >
                    <Trash2 />
                  </button>
                </div>
              )}
            </div>
          ))}
          {state.datasets.length === 0 && (
            <p className="muted">No game-specific datasets uploaded.</p>
          )}
          {state.me.role === "SEEKER" && (
            <form className="form-stack compact-form" onSubmit={upload}>
              <label>
                Dataset name
                <input value={name} onChange={(event) => setName(event.target.value)} required />
              </label>
              <label>
                Question category
                <select
                  value={category}
                  onChange={(event) => setCategory(event.target.value as DatasetCategory)}
                >
                  {["TENTACLES", "MATCHING", "MEASURING", "OTHER"].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                KML/KMZ file
                <input
                  type="file"
                  accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                  required
                />
              </label>
              <small>
                Maximum {Math.round(config.maxUploadBytes / 1024 / 1024)} MiB. Files are parsed in
                memory; only normalized GeoJSON is stored.
              </small>
              <button className="button secondary" disabled={busy}>
                Upload file
              </button>
            </form>
          )}
        </div>
        {state.me.role === "SEEKER" && (
          <div className="data-section">
            <div className="section-inline">
              <h3>Seeker markers</h3>
              <button className="text-button" onClick={onStartMarker}>
                Place on map
              </button>
            </div>
            {(state.seekerMarkers ?? []).map((marker) => (
              <div className="data-row" key={marker.id}>
                <div>
                  <strong>{marker.title}</strong>
                  <small>{marker.note || "No note"}</small>
                </div>
                <div className="row-actions">
                  <button className="text-button" onClick={() => onEditMarker(marker)}>
                    Edit
                  </button>
                  <button
                    className="icon-button danger small"
                    onClick={() => void deleteMarker(marker.id)}
                  >
                    <Trash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function GamePanel({
  state,
  busy,
  onClose,
  onStart,
  onEnd,
  onReset,
  onLeaveDevice,
}: {
  state: GameState;
  busy: boolean;
  onClose: () => void;
  onStart: (loadOsmPois: boolean) => Promise<void>;
  onEnd: () => void;
  onReset: () => void;
  onLeaveDevice: () => void;
}) {
  const [showOsmPrompt, setShowOsmPrompt] = useState(false);

  return (
    <div className="modal-backdrop align-end">
      <section className="sheet compact-sheet">
        <div className="sheet-header">
          <div>
            <p className="eyebrow">
              {state.game.seekerOnly ? `Seeker-only · ${state.game.phase}` : state.game.phase}
            </p>
            <h2>Players & game</h2>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </div>
        <div className="player-list">
          {state.players.map((player) => (
            <div className="player-row" key={player.id}>
              <span className={player.connected ? "presence online" : "presence"} />
              <strong>
                {player.displayName}
                {player.id === state.me.id ? " (you)" : ""}
              </strong>
              <span>{player.role}</span>
            </div>
          ))}
        </div>
        {state.me.role === "SEEKER" && state.game.phase === "LOBBY" && (
          <>
            <button
              className="button primary wide"
              disabled={busy}
              onClick={() =>
                state.datasets.length === 0 ? setShowOsmPrompt(true) : void onStart(false)
              }
            >
              <Play size={18} />
              {busy ? "Starting…" : "Start game"}
            </button>
            {showOsmPrompt && state.datasets.length === 0 && (
              <div className="osm-poi-prompt" role="group" aria-label="OpenStreetMap POI fallback">
                <div className="osm-poi-prompt-copy">
                  <Database size={20} />
                  <div>
                    <strong>No map datasets selected</strong>
                    <p>
                      Load named points of interest from OpenStreetMap for this game? They are
                      removed automatically when the game ends.
                    </p>
                  </div>
                </div>
                <button
                  className="button primary wide"
                  disabled={busy}
                  onClick={() => void onStart(true)}
                >
                  {busy ? "Loading OSM POIs…" : "Load OSM POIs & start"}
                </button>
                <button
                  className="button secondary wide"
                  disabled={busy}
                  onClick={() => void onStart(false)}
                >
                  Start without POIs
                </button>
                <button
                  className="button subtle wide"
                  disabled={busy}
                  onClick={() => setShowOsmPrompt(false)}
                >
                  Cancel
                </button>
              </div>
            )}
            <button className="button danger-button wide" disabled={busy} onClick={onReset}>
              <Trash2 size={17} />
              Discard setup
            </button>
          </>
        )}
        {state.me.role === "SEEKER" && !["LOBBY", "ENDED"].includes(state.game.phase) && (
          <button className="button danger-button wide" disabled={busy} onClick={onEnd}>
            <Square size={17} />
            End game
          </button>
        )}
        {state.me.role === "SEEKER" && state.game.phase === "ENDED" && (
          <button className="button danger-button wide" disabled={busy} onClick={onReset}>
            <Trash2 size={17} />
            Clear current game
          </button>
        )}
        <button className="button subtle wide" disabled={busy} onClick={onLeaveDevice}>
          <Settings2 size={17} />
          Forget identity on this device
        </button>
      </section>
    </div>
  );
}
